import { ProductListingProduct } from "@/types/productListing";
import {
  ConfigurationRequirementsType,
  ReasonForUnavailability,
} from "@packages/product-list/prodcat-base-types";
import {
  FinancialProductType,
  Product,
  Variant,
} from "@packages/product-list/types";

const getUnavailableReason = (
  product: ProductListingProduct,
): ReasonForUnavailability | null => {
  if (product.outOfStock) {
    return "outOfStock";
  } else if (product.isInStoreOnly) {
    return "inStoreOnly";
  }
  return null;
};

export function transformProduct(
  productListingProduct: ProductListingProduct,
): Product {
  const getConfigurationRequirements = (
    product: ProductListingProduct,
  ): ConfigurationRequirementsType[] => {
    const configurationRequirements: ConfigurationRequirementsType[] = [];

    if (product.type === "m2mRollerBlind") {
      configurationRequirements.push("MadeToMeasureRollerBlind");
    } else if (product.type === "m2mFabric") {
      configurationRequirements.push(
        "MadeToMeasureCurtain",
        "MadeToMeasureRomanBlind",
      );
    } else if (product.type === "asaf") {
      configurationRequirements.push("AnyShapeAnyFabricFurniture");
    }

    return configurationRequirements;
  };

  const configurationRequirements = getConfigurationRequirements(
    productListingProduct,
  );

  const credit = getCreditIllustration(productListingProduct);

  const unavailableReason = getUnavailableReason(productListingProduct);
  const variants =
    productListingProduct?.colorSwatches?.map((swatch) => {
      return {
        id: swatch.id,
        pdpURL: {
          url: swatch.canonicalURL,
        },
        aliases: {
          skuId: swatch.skuId || "",
        },
        availability: {
          availableToOrder: swatch.isAvailable,
          shipFromStore: productListingProduct.shipFromStore,
          ...(unavailableReason && { unavailableReason }),
        },
        colour: { colour: swatch.color },
        colourSwatch: swatch.colorSwatchUrl,
        customerNotifiableEvents:
          productListingProduct.customerNotifiableEvents,
        differentiators: {
          colour: swatch.color,
        },
        images: {
          primary: swatch.imageUrl,
          alternatives:
            swatch.imageUrl?.split("?")[0] ===
            productListingProduct.image?.split("?")[0]
              ? productListingProduct.alternativeImageUrls || []
              : [],
        },
        ...(productListingProduct.energy && {
          energy: {
            rating: {
              value: productListingProduct?.energy?.rating.value,
              legacy: productListingProduct?.energy?.rating.legacy,
            },
            informationSheet: productListingProduct?.energy?.informationSheet,
            label: productListingProduct?.energy?.label,
          },
        }),
        returnsOutlet: productListingProduct.isOutlet?.toString() || "",
        sellingRestrictions: {
          age: {
            minimumAge: productListingProduct.ageRestriction,
            bladedArticle: productListingProduct.isBladedArticle,
          },
        },
      } satisfies Variant;
    }) || [];

  const sets = productListingProduct.isProductSet
    ? {
        sets: [
          {
            url: productListingProduct.url,
            title: productListingProduct.title,
          },
        ],
      }
    : { sets: [] };

  const product: Product = {
    aliases: {
      pimProductId: productListingProduct.productId,
    },
    attributes: productListingProduct.attributes,
    variantPriceRange: productListingProduct.variantPriceRange,
    compare: productListingProduct.compare,
    configurationRequirements: {
      type: configurationRequirements,
    },
    variants,
    brand: {
      name: productListingProduct.brand,
    },
    defaultVariant: {
      id: productListingProduct.defaultVariantId,
    },
    pdpURL: {
      url: productListingProduct.url,
    },
    reviewSummary: {
      averageRating: productListingProduct.averageRating,
      numberOfReviews: productListingProduct.reviews,
    },
    title: productListingProduct.title,
    initialVariantId: variants[0]?.id,
    ...(credit && { credit }),
    messaging: productListingProduct.messaging,
    availability: productListingProduct.availability,
    unitOfMeasure: "EACH",
    ...sets,
    advertising: productListingProduct.advertising,
  };

  return product;
}

function getCreditIllustration(
  product: ProductListingProduct,
): Product["credit"] {
  const { credit } = product;
  if (!credit) return undefined;

  const { available, illustration } = credit;

  const standardisedIllustration = illustration
    ? {
        ...illustration,
        financialProductType: standardiseFinancialProductType(
          illustration.financialProductType,
        ),
      }
    : undefined;

  return {
    available,
    illustration: standardisedIllustration,
  };
}

function standardiseFinancialProductType(
  fpt: string,
): FinancialProductType | undefined {
  return fpt === "IBC" || fpt === "IFC" ? fpt : undefined;
}
